import TeceoLogo from '@components/v2/ui/TeceoLogo/TeceoLogo';
import { Box, Container, Grid, Paper } from '@mui/material';
import MuiLink from '@mui/material/Link';
import classnames from 'classnames';
import * as React from 'react';
import LangEnum from '../../lang/LangEnum';
import { useTranslation } from '../../modules/translation/TranslationContext';
import { LoginPageTestIds } from '../../utils/testIds/LoginPage.testIds';
import ButtonWithFeddback, { ButtonStatusVariant } from '../ButtonWithFeddback';
import { privacyPoliticsUrl, termsOfUseUrl } from '../PrivacyPolitics';
import RHFTextField from '../ReactHookForm/RHFTextField';
import useLoginFormStyles from './LoginForm.styles';
import useLoginForm, { LoginFormValues } from './useLoginForm';

interface LoginFormSlots {
  afterSubmit?: React.ReactNode;
}

interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => Promise<void>;
  status: ButtonStatusVariant;
  background?: string;
  children?: React.ReactNode;
  slots?: LoginFormSlots;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, status, background, children, slots }) => {
  const { classes } = useLoginFormStyles({ background });
  const { translate } = useTranslation();
  const { formMethods, handleSubmit } = useLoginForm({ onSubmit });
  const { control } = formMethods;

  return (
    <div className={classes.background}>
      <Paper className={classnames(classes.paper, classes.root)} elevation={0}>
        <Container maxWidth="xs" disableGutters>
          <Grid container justifyContent="center" spacing={3}>
            <Grid size={8}>
              <TeceoLogo />
            </Grid>
            <Grid size={12}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2} direction="column">
                  <Grid>
                    <RHFTextField
                      name="email"
                      control={control}
                      testId={LoginPageTestIds.LOGIN_EMAIL}
                      textFieldProps={{
                        type: 'email',
                        fullWidth: true,
                        label: translate(LangEnum.LOGIN_EMAIL),
                        variant: 'outlined',
                        size: 'large',
                      }}
                    />
                  </Grid>
                  <Grid>
                    <RHFTextField
                      name="password"
                      control={control}
                      testId={LoginPageTestIds.LOGIN_PASSWORD}
                      textFieldProps={{
                        type: 'password',
                        fullWidth: true,
                        label: translate(LangEnum.LOGIN_PASSWORD),
                        variant: 'outlined',
                        size: 'large',
                      }}
                    />
                  </Grid>
                  {children}
                  <Grid size={12}>
                    <ButtonWithFeddback
                      loadingLabel={translate(LangEnum.LOGIN_BUTTONFEEDBACK_WAIT)}
                      successLabel={translate(LangEnum.LOGIN_BUTTONFEEDBACK_WELCOME)}
                      errorLabel={translate(LangEnum.LOGIN_BUTTONFEEDBACK_OOPS)}
                      status={status}
                      variant="contained"
                      size="large"
                      color="primary"
                      type="submit"
                      fullWidth
                    >
                      {translate(LangEnum.LOGIN_LOGIN)}
                    </ButtonWithFeddback>
                  </Grid>
                  {slots?.afterSubmit && <Grid size={12}>{slots.afterSubmit}</Grid>}
                </Grid>
              </form>
            </Grid>
          </Grid>
        </Container>
      </Paper>
      <Box className={classes.container}>
        <MuiLink
          href={privacyPoliticsUrl}
          target="_blank"
          rel="noreferrer"
          className={classes.redirectLink}
          underline="hover"
        >
          {translate(LangEnum.LOGIN_PRIVACY_POLICY)}
        </MuiLink>
        <MuiLink
          href={termsOfUseUrl}
          target="_blank"
          rel="noreferrer"
          className={classes.redirectLink}
          underline="hover"
        >
          {translate(LangEnum.LOGIN_TERMS_OF_USE)}
        </MuiLink>
      </Box>
    </div>
  );
};

export default LoginForm;
